import React, { useState, useCallback } from "react";
import { X, Mail } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PortalDropdownBox from "./PortalDropDownBox";
import AccessLevelTable from "./AccessLevelTable";
import { addAdminUser } from "../../actions/ManageUserAction";

const MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "order", label: "Orders" },
    {key: "product", label: "Products" },
  { key: "simSwap", label: "Sim swap" },
  { key: "customer", label: "Customers" },
  { key: "referralCode", label: "Referral code" },
  { key: "manageUser", label: "Manage users" },
];

const AddNewUser = ({ closeModal }) => {
  const [customerName, setCustomerName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [permissions, setPermissions] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const isValidStatus = (status) => {
    return ["ACTIVE", "INACTIVE"].includes(status);
  }

  const prepareAccessLevels = (permissions) => {
    const accessLevels = {};
    Object.keys(permissions).forEach((moduleKey) => {
      const selected = Object.entries(permissions[moduleKey])
        .filter(([perm, value]) => value)
        .map(([perm]) => perm);

      if (selected.length > 0) {
        accessLevels[moduleKey] = selected;
      }
    });
    return accessLevels;
  };

  const handleAddUser = useCallback(async () => {
    if (!customerName.trim()) {
      toast.error("Please enter the customer name.");
      return;
    }
    if (!email.trim()) {
      toast.error("Please enter an email address.");
      return;
    }
    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if(!isValidStatus(status)) {
      toast.error("Please select a status.");
      return;
    }

     // Check if any access levels are selected
    const accessLevels = prepareAccessLevels(permissions);
    if (!accessLevels || Object.keys(accessLevels).length === 0) {
      toast.error("No valid access levels found");
      return;
    }

    const payload = {
      name: customerName.trim(),
      email: email.trim(),
      accessLevels,
      status: status.toLowerCase(),
    };

    console.log("Payload Preview:", payload);

    try {
      setIsLoading(true);
      
      const userId = localStorage.getItem("adminId");

      if (!userId) {
        toast.error("User ID not found. Please log in again.");
        return;
      }

      const response = await addAdminUser(payload, userId);

      if (response.success) {
        toast.success(response?.message || "Admin added successfully.");
        closeModal();
        window.location.reload();
      } else {
        toast.error(response?.message || "Failed to add user.");
      }
    } catch (error) {
      toast.error("An error occurred while adding the user.");
    } finally {
      setIsLoading(false);
    }
  }, [customerName, email, status, permissions, closeModal]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      {/* Mobile close button above modal, top right */}
      <button
        onClick={closeModal}
        className="block md:hidden fixed z-50 p-2 bg-white rounded-full shadow border border-gray-200"
        style={{
          top: '16px',
          right: '16px',
        }}
      >
        <X size={28} />
      </button>
      <div className="relative bg-white p-6 rounded-[20px] w-[800px] max-h-[650px] shadow-lg flex flex-col mx-4 sm:mx-0" style={{maxWidth: '95vw'}}>
        {/* Desktop close button in previous position */}
        <button
          onClick={closeModal}
          className="hidden md:block absolute -top-5 -right-10 p-2 bg-white rounded-full"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="flex justify-between items-center pb-4 mb-6 border-b sticky top-0 bg-white z-10 rounded-t-[20px]">
          <h2 className="text-3xl font-bold">Add New User</h2>
          <div className="gap-3 flex">
            <button
              onClick={closeModal}
              className="secondary_button inactive"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleAddUser}
              className={`secondary_button ${
                isLoading ? "opacity-50 cursor-not-allowed" : "active"
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "Create"}
            </button>
          </div>
        </div>

        {/* Form */}
        <div
          className="overflow-y-auto flex-1"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>
            {`
              div::-webkit-scrollbar {
                display: none;
              }
              @media (max-width: 768px) {
                .grid-cols-2 {
                  grid-template-columns: 1fr;
                }
                .w-[800px] {
                  width: 95%;
                  max-width: 320px;
                }
                .flex-between {
                  flex-direction: column;
                  gap: 1rem;
                }
                .p-6 {
                  padding: 1rem 1.25rem;
                }
                .gap-4 {
                  gap: 0.5rem;
                }
                .text-3xl {
                  font-size: 1.5rem;
                }
                .w-40 {
                  width: 100%;
                }
              }
            `}
          </style>

          <div className="grid grid-cols-2 gap-4">
            {/* Admin Name */}
            <div>
              <label className="font-medium text-sm leading-5 text-gray-700">
                User name
              </label>
              <input
                type="text"
                placeholder="Enter name"
                className="primary_input text-text1 w-full p-2 border rounded"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Email */}
            <div>
              <label className="font-medium text-sm leading-5 text-gray-700">
                Email address
              </label>
              <div className="flex items-center border border-gray-300 pl-3 rounded bg-gray-50">
                <Mail color="#6B7280" className="mr-3" />
                <input
                  type="email"
                  placeholder="Enter email address"
                  className="secondary_input text-text1 w-full border-l rounded-md rounded-l-none border-gray-300 bg-white focus:outline-none focus:ring-0"
                  value={email} // Fixed binding
                  onChange={(e) => setEmail(e.target.value)} // Fixed binding
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Access Level Table */}
          <AccessLevelTable
            modules={MODULES.map((m) => m)}
            value={permissions}
            onChange={setPermissions}
          />

          {/* Status Dropdown */}
          <div className="flex justify-between items-center bg-gray-50 p-2 rounded-md mt-[20px] mb-[20px]">
            <div>
              <p className="font-medium">Update status</p>
              <p className="text-sm text-gray-500">
                This will affect the status of the particular user.
              </p>
            </div>
            <div className="w-40">
              <PortalDropdownBox
                optionList={["ACTIVE", "INACTIVE"]}
                  styleName="rounded-md text-sm font-normal text-text1 uppercase focus:outline-none focus:ring-0 w-full mt-1"
                value={status}
                handleChange={(selectedValue) => setStatus(selectedValue)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewUser;
